import { createUseStyles } from 'react-jss';

import { mixins } from 'resources/styles/mixins';

const useStyles = createUseStyles(() => ({
  '@global': {
    '#root': {
      height: '100vh',
    },

    '*': {
      boxSizing: 'border-box',
    },

    body: {
      fontFamily: "'Roboto', sans-serif",
      background: 'var(--background-default)',
      color: 'var(--text-primary)',
      margin: '0',
      overflow: 'hidden',

      '&::selection': {
        color: 'var(--clr-white)',
        backgroundColor: 'var(--primary-main)',
      },
    },

    div: {
      outline: 'none',
    },

    button: {
      outline: 'none',
    },

    'h1, h2, h3, h4': {
      color: 'var(--text-primary)',
    },

    'b, strong': {
      fontWeight: 500,
    },

    p: {
      margin: 0,
    },

    a: {
      textDecoration: 'none',
    },

    '.hoverable-action': {
      cursor: 'pointer',
      color: 'var(--primary-main)',

      '&:hover': {
        color: 'var(--button-active)',
      },
    },

    '.hoverable-paper': {
      cursor: 'pointer',
      background: 'var(--background-paper)',
      borderRadius: '4px',
      boxShadow: 'var(--shadows-3)',

      '&:hover': {
        boxShadow: 'var(--shadows-8)',
      },
    },

    'div > .listItemWrapper': {
      color: 'var(--text-primary)',
      backgroundColor: 'var(--background-paper)',

      '&:hover': {
        backgroundColor: 'var(--sectionHover)',
      },

      '&:not(:last-child)': {
        borderBottom: `1px solid var(--background-default)`,
      },
    },

    '.line-break': {
      whiteSpace: 'pre-line',
    },

    '.wide': {
      width: '100%',
    },

    '.error': {
      textAlign: 'end',
      color: 'var(--error-main)',
      position: 'absolute',
      right: '0',
      fontSize: '12px',
      lineHeight: 'unset',
    },

    '.error__bottom': {
      bottom: '-17px',
    },

    '.clickableIcon': {
      cursor: 'pointer',

      '& svg': {
        color: 'var(--primary-main)',
      },

      '&:hover svg': {
        color: 'var(--action-hover)',
      },
    },

    '.djs > div a': {
      display: 'flex',
      alignItems: 'center',
    },

    '#appleid-signin': {
      position: 'relative',
      width: '20px',
      height: '20px',
      overflow: 'hidden',
      cursor: 'pointer',

      '& img': {
        position: 'absolute',
        top: '-40px',
        left: '-20px',
      },
    },

    '.position-absolute': {
      position: 'absolute',
    },

    '.mainText': {
      fontFamily: 'Roboto',
      fontSize: '18px',
      color: 'var(--text-primary)',
      textAlign: 'center',
    },

    '.blackText': {
      fontFamily: 'Roboto',
      fontSize: '16px',
      color: 'var(--text-primary)',
      textAlign: 'center',
    },

    '.errorMessage': {
      fontSize: '12px',
      color: 'var(--error-main)',
    },

    '.go-back': {
      zIndex: 7,
      top: '14.5px',

      '& svg': {
        marginRight: '15px',
        width: '21px',
        minWidth: '21px',
        color: 'var(--primary-main)',

        '&:hover': {
          color: 'var(--action-hover)',
        },
      },

      '& a': {
        zIndex: 7,
        marginLeft: '20px',
        display: 'flex',
        fontSize: '20px',
        alignItems: 'center',
        color: `var(--text-primary)!important`,
      },

      '& span': {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: '240px',
      },
    },

    '.header-icon': {
      minWidth: '21px',
      marginRight: '16px',
      cursor: 'pointer',
    },

    '.iconWrap': {
      display: 'flex',
      alignItems: 'center',

      '& svg': {
        marginRight: '16px',
        cursor: 'pointer',
        color: 'var(--primary-main)',

        '&:hover': {
          color: 'var(--action-hover)',
        },
      },

      '& .reloadIcon': {
        cursor: 'pointer',
      },
    },

    '.v-header-button': {
      borderRadius: '4px',
      width: '172px',
      minWidth: 'unset',
      height: '28px',
      fontSize: '16px',
      fontWeight: 500,
      boxShadow: 'var(--shadows-4)',

      '& svg': {
        width: '16px',
        height: '16px',
      },

      '&:hover': {
        boxShadow: 'var(--shadows-8)',
      },
    },

    '.v-header-icon': {
      width: '28px',
      minWidth: '28px',
      height: '28px',
      padding: 6,
    },

    '.hidden': {
      position: 'absolute',
      opacity: 0,
      cursor: 'pointer',
      height: 0,
      width: 0,
    },

    '.rdw-editor-toolbar': {
      border: 'none',
      margin: '0',
      padding: '0',

      '& .rdw-history-wrapper': {
        margin: '0',
      },

      '& .rdw-option-wrapper': {
        margin: '0',
        padding: '0',
        border: 'none',
        cursor: 'pointer',

        '&:hover': {
          boxShadow: 'none',
        },
      },
    },

    '.react-code-input': {
      display: 'flex!important',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',

      '& input': {
        color: 'var(--text-secondary)',
        backgroundColor: 'var(--background-default)',

        '&::-webkit-inner-spin-button': {
          '-webkit-appearance': 'none',
        },

        '&[type=number]': {
          '-moz-appearance': 'textfield',
        },
      },

      '&.invalid': {
        '& input': {
          border: `1px solid var(--error-light)!important`,
        },
      },
    },

    '.shareButtonOption': {
      padding: '0 10px!important',
    },

    '.viboWizard': {
      '& > div, & > div > div, & > div > div > div': {
        height: '100%',
      },
    },

    '.ReactCrop': {
      '& .ReactCrop__crop-selection': {
        border: `1px solid var(--primary-main)`,
        boxShadow: '0 0 0 9999em rgba(255,255,255,.7)',
      },

      '& .ReactCrop__rule-of-thirds-vt, & .ReactCrop__rule-of-thirds-hz': {
        display: 'none',
      },

      '& .ReactCrop__drag-handle': {
        '&:after': {
          border: `1px solid var(--primary-main)`,
          borderRadius: '50%',
          backgroundColor: 'var(--background-paper)',
        },

        '&.ord-n, &.ord-e, &.ord-s, &.ord-w': {
          display: 'none',
        },
      },
    },

    '.react-tel-input': {
      '& .country-list': {
        overflowY: 'scroll',
        zIndex: 1,
        listStyle: 'none',
        position: 'absolute',
        padding: '0',
        margin: '15px 0 10px -1px',
        boxShadow: 'var(--shadows-4)',
        backgroundColor: 'var(--background-dropDown)',
        borderRadius: '4px',

        '& .search': {
          backgroundColor: 'var(--background-dropDown)',

          '& .search-box': {
            color: 'inherit',
            backgroundColor: 'var(--background-input)',
            border: `1px solid var(--primary-main)`,
          },
        },

        '& .no-entries-message': {
          color: 'var(--text-secondary)',
        },

        '& .country': {
          color: 'var(--text-secondary)',

          '& .dial-code': {
            color: 'var(--text-hint)',
          },
        },

        '& .country.highlight': {
          backgroundColor: 'var(--background-default)',
        },

        '& .country:hover': {
          backgroundColor: 'var(--background-photo)',
        },
      },

      '& .form-control': {
        color: 'inherit',

        '&:disabled': {
          cursor: 'not-allowed',
        },

        '&, &:focus': {
          backgroundColor: 'transparent',
        },
      },
    },

    svg: {
      '&.tidal path': {
        fill: 'var(--text-primary)',
      },
    },

    // NEW GLOBAL STYLES
    '.Toastify__toast-container': {
      width: '190px',

      '&.Toastify__toast-container--top-right': {
        top: '3em',
        right: '3em',

        '@media screen and (min-width: 1441px)': {
          top: '4em',
          right: '4em',
        },
      },

      '@media screen and (min-width: 1441px)': {
        width: '230px',
      },
    },

    '.toastNotify': {
      padding: '4px 4px 4px 10px',
      minHeight: '48px',
      backgroundColor: 'var(--background-paper)',
      boxShadow: 'var(--shadows-4)',

      '&.Toastify__toast--success': {
        color: 'var(--success-main)',
      },

      '&.Toastify__toast--error': {
        color: 'var(--error-main)',

        '& .toastNotifyProgress': {
          backgroundColor: 'var(--error-main)',
        },
      },

      '& .viboIcon': {
        color: 'inherit',
        fontSize: '14px',

        '@media screen and (min-width: 1441px)': {
          fontSize: '16px',
        },

        '&.close': {
          color: 'var(--text-hint)',
          fontSize: '10px',

          '@media screen and (min-width: 1441px)': {
            fontSize: '12px',
          },
        },
      },

      '& .Toastify__toast-icon': {
        width: 'auto',
        marginInlineEnd: '6px',
      },

      '& .toastNotifyProgress': {
        height: '2px',
      },

      '& .toastNotifyBody': {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: '7px 0',

        '& div': {
          fontWeight: 500,
          fontSize: '11px',
          lineHeight: '15.59px',
          fontFamily: 'Roboto, sans-serif',

          '@media screen and (min-width: 1441px)': {
            fontSize: '14px',
            lineHeight: '19.6px',
          },
        },

        '& svg': {
          width: '14px',
          height: '14px',
          fill: 'none',

          '@media screen and (min-width: 1441px)': {
            width: '16px',
            height: '16px',
          },
        },

        '@media screen and (min-width: 1441px)': {
          padding: '9px 0',
        },
      },
    },

    '.noClickableFilter': {
      ...mixins.noClickableFilter,
    },

    '.rc-dropdown': {
      fontFamily: "'Roboto', sans-serif",
    },

    '& .ql-container, & .ql-editor': {
      fontSize: '13px',
      lineHeight: '24px',

      '& h1, h2, h3, h4, h5, h6': {
        fontWeight: '400',
      },

      '& h1': {
        lineHeight: '32px',
      },

      '& h2': {
        lineHeight: '26px',
      },

      '@media screen and (min-width: 1441px)': {
        fontSize: '16px',
        lineHeight: '24px',
      },
    },

    '.viboSelect__menu-portal': {
      zIndex: '3!important',
    },

    '.react-datepicker__tab-loop': {
      position: 'fixed',
      zIndex: 9999,
    },

    '.content-fade-in': {
      opacity: 0,

      '&.ready': {
        animation: 'var(--fade-in)',

        '&._1': {
          animation: 'var(--fade-in-1)',
        },

        '&._2': {
          animation: 'var(--fade-in-2)',
        },

        '&._3': {
          animation: 'var(--fade-in-3)',
        },
      },
    },

    '@keyframes fade-in': {
      from: {
        opacity: 0,
      },
      to: {
        opacity: 1,
      },
    },

    '@keyframes fade-in-cycle': {
      '0%': { opacity: 1 },
      '10%': { opacity: 1 },
      '62.5%': { opacity: 0 },
      '100%': { opacity: 1 },
    },
  },
}));

export default useStyles;
